import { ChangeEvent, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { t } from "@lingui/macro";
import { useSnackbarContext } from "@WahooFitness/wahoo-offline-mfe";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import { EventType, SourcePage } from "@/services/Analytics/EventTypes";

// Brand names, so they aren't translated. The ids are what analytics receives.
export const WEARABLES = [
  { id: "google", name: "Google" },
  { id: "whoop", name: "Whoop" },
  { id: "garmin", name: "Garmin" },
  { id: "coros", name: "Coros" },
  { id: "polar", name: "Polar" },
  { id: "samsung_health", name: "Samsung Health" },
  { id: "suunto", name: "Suunto" },
  { id: "ultrahuman", name: "Ultrahuman" },
  { id: "fitbit", name: "Fitbit" },
  { id: "amazfit", name: "Amazfit" },
] as const;

// Analytics string parameters are capped at 100 characters.
export const MAX_PARAM_LENGTH = 100;

function useRequestWearables() {
  const navigate = useNavigate();
  const { logEvent } = useLogEvent();
  const { enqueueSnackbar } = useSnackbarContext();
  const [selected, setSelected] = useState<string[]>([]);
  const [otherWearable, setOtherWearable] = useState("");

  const toggleWearable = useCallback((id: string) => {
    setSelected((current) =>
      current.includes(id) ? current.filter((selectedId) => selectedId !== id) : [...current, id]
    );
  }, []);

  const handleOtherWearableChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setOtherWearable(event.target.value);
  }, []);

  const trimmedOther = otherWearable.trim();
  const canSubmit = selected.length > 0 || trimmedOther.length > 0;

  const submit = useCallback(() => {
    if (!canSubmit) return;
    // Analytics parameters can't be arrays, so the picks go as one list in the order shown.
    const picks = WEARABLES.filter(({ id }) => selected.includes(id)).map(({ id }) => id);
    logEvent("request_wearable_survey", {
      event_type: EventType.click,
      click_target: picks.join(","),
      ...(trimmedOther && { open_text: trimmedOther.slice(0, MAX_PARAM_LENGTH) }),
      source_page_name: SourcePage.request_wearables,
    });
    enqueueSnackbar({ message: t`Thanks for your request`, severity: "success" });
    navigate(-1);
  }, [canSubmit, enqueueSnackbar, logEvent, navigate, selected, trimmedOther]);

  const cancel = useCallback(() => navigate(-1), [navigate]);

  return {
    selected,
    toggleWearable,
    otherWearable,
    handleOtherWearableChange,
    canSubmit,
    submit,
    cancel,
  };
}

export default useRequestWearables;
